import { AppUser } from "../interfaces/IUsers";
import { createLog } from "../utils/firestore";
/**
 * Serwis logowania zdarzeń
 *
 * Zawiera funkcje do logowania działań użytkowników do kolekcji Firestore 'logs'.
 * Implementuje funkcjonalny wzorzec zgodny z guidelines projektu (bez class).
 * Wykorzystuje wzorce z firestore.tsx dla spójności architektury.
 *
 * Funkcjonalności:
 * - Logowanie zdarzeń do Firestore
 * - Obsługa błędów logowania
 * - Walidacja danych wejściowych
 * - Optymalizacja rozmiaru logów
 */

import {
  LogEntry,
  LogData,
  LogDataWithFullUser,
  LoggingResult,
  LoggingConfig,
  DEFAULT_LOGGING_CONFIG,
  EventType,
  LogAction,
  EntityType,
} from "../interfaces/ILogs";

/**
 * Podstawowa funkcja logowania zdarzenia do Firestore
 *
 * @param logData - Dane logu (bez id i timestamp)
 * @param config - Konfiguracja logowania (opcjonalna)
 * @returns Promise<LoggingResult> - Wynik operacji logowania
 */
export const logEvent = async (
  logData: LogData,
  config: LoggingConfig = DEFAULT_LOGGING_CONFIG
): Promise<LoggingResult> => {
  try {
    // Walidacja danych wejściowych
    const validationResult = validateLogData(logData);
    if (!validationResult.isValid) {
      const error = `Walidacja danych logu nie powiodła się: ${validationResult.error}`;
      console.error("❌ [LoggingService]", error);

      if (config.throwOnError) {
        throw new Error(error);
      }

      return { success: false, error };
    }

    // Optymalizacja rozmiaru stanów
    const optimizedLogData = optimizeLogData(logData, config);

    // Wykorzystanie funkcji z firestore.tsx
    const createdLog = await createLog(optimizedLogData);

    return {
      success: true,
      logId: createdLog.id,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Nieznany błąd podczas logowania";

    console.error("❌ [LoggingService] Błąd podczas logowania zdarzenia:", {
      error: errorMessage,
      eventType: logData.eventType,
      entityId: logData.entityId,
    });

    if (config.logErrorsToConsole) {
      console.error("❌ [LoggingService] Szczegóły błędu:", error);
    }

    if (config.throwOnError) {
      throw error;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Funkcja logowania z kontekstem użytkownika
 *
 * @param eventType - Typ zdarzenia
 * @param action - Typ akcji
 * @param entityType - Typ encji
 * @param entityId - ID encji
 * @param beforeState - Stan przed operacją
 * @param afterState - Stan po operacji
 * @param user - Dane użytkownika
 * @param success - Czy operacja się powiodła
 * @param errorMessage - Komunikat błędu (opcjonalny)
 * @param metadata - Dodatkowe metadane (opcjonalne)
 * @param config - Konfiguracja logowania (opcjonalna)
 * @returns Promise<LoggingResult> - Wynik operacji logowania
 */
export const logUserAction = async (
  eventType: EventType,
  action: LogAction,
  entityType: EntityType,
  entityId: string,
  beforeState: any,
  afterState: any,
  user: AppUser,
  success: boolean,
  errorMessage?: string,
  metadata?: any,
  config: LoggingConfig = DEFAULT_LOGGING_CONFIG
): Promise<LoggingResult> => {
  const logData: LogData = {
    userId: user.id,
    userEmail: user.email,
    userFirstName: user.firstName,
    userLastName: user.lastName,
    userOrganizationName: user.organizationName || "",
    eventType,
    action,
    entityType,
    entityId,
    beforeState,
    afterState,
    success,
    errorMessage,
    metadata,
  };

  return logEvent(logData, config);
};

/**
 * Funkcja logowania z pełnymi danymi użytkownika
 *
 * @param logData - Dane logu z pełnymi danymi użytkownika
 * @param config - Konfiguracja logowania (opcjonalna)
 * @returns Promise<LoggingResult> - Wynik operacji logowania
 */
export const logEventWithFullUser = async (
  logData: LogDataWithFullUser,
  config: LoggingConfig = DEFAULT_LOGGING_CONFIG
): Promise<LoggingResult> => {
  return logEvent(logData, config);
};

/**
 * Walidacja danych logu
 *
 * @param logData - Dane do walidacji
 * @returns Obiekt z wynikiem walidacji
 */
const validateLogData = (logData: LogData): { isValid: boolean; error?: string } => {
  // Sprawdzenie wymaganych pól
  if (!logData.userId) {
    return { isValid: false, error: "userId jest wymagane" };
  }

  if (!logData.userEmail) {
    return { isValid: false, error: "userEmail jest wymagane" };
  }

  if (!logData.userFirstName) {
    return { isValid: false, error: "userFirstName jest wymagane" };
  }

  if (!logData.userLastName) {
    return { isValid: false, error: "userLastName jest wymagane" };
  }

  if (!logData.userOrganizationName) {
    return { isValid: false, error: "userOrganizationName jest wymagane" };
  }

  if (!logData.eventType) {
    return { isValid: false, error: "eventType jest wymagane" };
  }

  if (!logData.action) {
    return { isValid: false, error: "action jest wymagane" };
  }

  if (!logData.entityType) {
    return { isValid: false, error: "entityType jest wymagane" };
  }

  if (!logData.entityId) {
    return { isValid: false, error: "entityId jest wymagane" };
  }

  if (typeof logData.success !== "boolean") {
    return { isValid: false, error: "success musi być boolean" };
  }

  // Sprawdzenie czy eventType jest prawidłowy
  if (!Object.values(EventType).includes(logData.eventType)) {
    return { isValid: false, error: `Nieprawidłowy eventType: ${logData.eventType}` };
  }

  // Sprawdzenie czy action jest prawidłowy
  if (!Object.values(LogAction).includes(logData.action)) {
    return { isValid: false, error: `Nieprawidłowy action: ${logData.action}` };
  }

  // Sprawdzenie czy entityType jest prawidłowy
  if (!Object.values(EntityType).includes(logData.entityType)) {
    return { isValid: false, error: `Nieprawidłowy entityType: ${logData.entityType}` };
  }

  return { isValid: true };
};

/**
 * Optymalizacja danych logu
 *
 * @param logData - Dane do optymalizacji
 * @param config - Konfiguracja optymalizacji
 * @returns Zoptymalizowane dane logu
 */
const optimizeLogData = (logData: LogData, config: LoggingConfig): LogData => {
  const optimized = { ...logData };

  // Usuwanie undefined wartości (Firestore nie akceptuje undefined)
  if (optimized.errorMessage === undefined) {
    delete optimized.errorMessage;
  }
  if (optimized.metadata === undefined) {
    delete optimized.metadata;
  }

  // Optymalizacja beforeState
  if (optimized.beforeState) {
    const beforeStateSize = JSON.stringify(optimized.beforeState).length;
    if (beforeStateSize > config.maxStateSize) {
      if (config.compressLargeStates) {
        // Kompresja dużych stanów - zapisujemy tylko kluczowe pola
        optimized.beforeState = compressState(optimized.beforeState);
      } else {
        // Usuwamy duże stany
        optimized.beforeState = { error: "State too large, removed for optimization" };
      }
    }
  }

  // Optymalizacja afterState
  if (optimized.afterState) {
    const afterStateSize = JSON.stringify(optimized.afterState).length;
    if (afterStateSize > config.maxStateSize) {
      if (config.compressLargeStates) {
        // Kompresja dużych stanów - zapisujemy tylko kluczowe pola
        optimized.afterState = compressState(optimized.afterState);
      } else {
        // Usuwamy duże stany
        optimized.afterState = { error: "State too large, removed for optimization" };
      }
    }
  }

  return optimized;
};

/**
 * Kompresja stanu - zachowuje tylko kluczowe pola
 *
 * @param state - Stan do kompresji
 * @returns Skompresowany stan
 */
const compressState = (state: any): any => {
  if (!state || typeof state !== "object") {
    return state;
  }

  // Lista kluczowych pól do zachowania
  const keyFields = ["id", "name", "title", "status", "firstName", "lastName", "email"];

  const compressed: any = {};

  for (const field of keyFields) {
    if (state[field] !== undefined) {
      compressed[field] = state[field];
    }
  }

  // Dodaj informację o kompresji
  compressed._compressed = true;
  compressed._originalKeys = Object.keys(state);

  return compressed;
};

/**
 * Funkcja pomocnicza do logowania błędów
 *
 * @param error - Błąd do zalogowania
 * @param context - Kontekst błędu
 * @param config - Konfiguracja logowania (opcjonalna)
 */
export const logError = async (
  error: Error,
  context: string,
  config: LoggingConfig = DEFAULT_LOGGING_CONFIG
): Promise<void> => {
  if (config.logErrorsToConsole) {
    console.error(`❌ [LoggingService] Błąd w kontekście: ${context}`, error);
  }
};

/**
 * Funkcja pomocnicza do logowania informacji
 *
 * @param message - Wiadomość do zalogowania
 * @param data - Dodatkowe dane (opcjonalne)
 */
export const logInfo = (message: string, data?: any): void => {
  // Logging disabled for production
};

/**
 * Funkcja pomocnicza do logowania sukcesu
 *
 * @param message - Wiadomość do zalogowania
 * @param data - Dodatkowe dane (opcjonalne)
 */
export const logSuccess = (message: string, data?: any): void => {
  // Logging disabled for production
};
